import DashboardIcon from "@mui/icons-material/Dashboard";
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import SummarizeIcon from '@mui/icons-material/Summarize';
import InsertChartIcon from '@mui/icons-material/InsertChart';
import { Box } from "@mui/material";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useEffect, useRef, useState, useLayoutEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastContainer } from 'react-toastify';
import Footer from "./components/Footer/Footer";
import Header from "./components/Header/Header";
import MainContent from "./components/MainContent/MainContent";
import PreventBackNavigation from "./pages/PreventBackNavigation";
import { setTitle } from "./redux/actions/dashboardTitle";
import { ModuleRegistry } from "ag-grid-community";
import { AgChartsEnterpriseModule } from 'ag-charts-enterprise';
import { AllEnterpriseModule, LicenseManager } from "ag-grid-enterprise";
import { InteractionStatus } from "@azure/msal-browser";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { Loader } from "./components/common/Loader";
import { checkTokenExpiry } from "./utils/authUtils";
import { getUserRoleEndpoint } from "./common/endpoints";
import { getHeaderRoles } from "./redux/actions/GetHeaderRolesSlice";
import { jwtDecode } from "jwt-decode";
import UnauthorizedApp from "./routes/UnauthorizedApp";
import { setForecastUpdates } from './redux/actions/forecastEmailSlice'

// LicenseManager.setLicenseKey("Using_this_{AG_Grid}_Enterprise_key_{AG-089652}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{The_Coca_Cola_Company}_is_granted_a_{Multiple_Applications}_Developer_License_for_{20}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_{AG_Grid}_Enterprise___This_key_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{31_August_2026}____[v3]_[01]_MTc4ODEzMDgwMDAwMA==cb959b9bfde93226378489360e0ff3d9");
ModuleRegistry.registerModules([AllEnterpriseModule.with(AgChartsEnterpriseModule)]);
LicenseManager.setLicenseKey("Using_this_{AG_Charts_and_AG_Grid}_Enterprise_key_{AG-065772}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{The_Coca_Cola_Company}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{Topline_Forecast}_only_for_{6}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_working_on_{Topline_Forecast}_need_to_be_licensed___{Topline_Forecast}_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Charts_and_AG_Grid}_Enterprise_versions_released_before_{21_September_2025}____[v3]_[0102]_MTc1ODQwOTIwMDAwMA==372521b73ce277d3fad471863dd69994");

const theme = createTheme({
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {

          '&.Mui-focused .MuiOutlinedInput-notchedOutline ': {
            borderColor: '#000',  // Your desired border color
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            borderColor: '#000',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            color: '#000',
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            color: '#000',
          },
        },
      },
    },
    MuiMultiSectionDigitalClockSection: {
      styleOverrides: {
        item: {
          "&.Mui-selected": {
            backgroundColor: '#000',
          },
          "&.Mui-selected:hover": {
            backgroundColor: '#797979'
          }
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          color: '#797979'
        },
      },
    },

  },
});

function App() {
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const title = useSelector((state) => state?.dashboardDetails?.title);
  const tabDetails = useSelector((state) => state?.dashboardDetails?.tabDetails);
  const [menuItems, setMenuItems] = useState([]);
  const [userRole, setUserRole] = useState("");
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts, inProgress } = useMsal();
  const [isLoading, setIsLoading] = useState(true); // Loading state

  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return;

    instance
      .handleRedirectPromise()
      .then((response) => {
        if (response && response.accessToken) {
          processLogin(response);
        } else if (accounts.length === 0) {
          handleLogin();
        } else {
          const accessToken = localStorage.getItem("accessToken");
          if (!accessToken) {
            instance.acquireTokenSilent({
              scopes: [process.env.REACT_APP_CLIENT_ID + "/.default"],
              account: accounts[0],
            }).then(processLogin)
              .catch(() => handleLogin());
          } else {
            const isExpired = checkTokenExpiry(accessToken);
            if (isExpired) {
              localStorage.clear();
              handleLogin();
            } else {
              getUserRoles();
            }
          }
        }
      })
      .catch((err) => console.error("Redirect Error:", err));
  }, [instance, accounts, inProgress]);

  const extractUserInfoFromToken = (accessToken) => {
    if (!accessToken) return { email: null, name: null, decoded: null };

    const decoded = jwtDecode(accessToken);

    // Email priority logic
    let email;

    // Fore internal coke users use unique id from token as email
    //else give priority to email
    let userUniqueId = decoded?.unique_name || decoded?.upn;
    userUniqueId = userUniqueId?.trim()?.toLowerCase();
    if (userUniqueId && (userUniqueId?.endsWith("@testko.com")
      || userUniqueId?.endsWith("@coca-cola.com"))) {
      email = userUniqueId;
    } else {
      email = decoded?.email?.trim()?.toLowerCase() ||
        userUniqueId ||
        null;
    }

    // Name priority logic
    const name =
      decoded?.name ||
      (decoded?.given_name && decoded?.family_name
        ? `${decoded.given_name} ${decoded.family_name}`
        : null) ||
      decoded?.preferred_username ||
      email ||
      null;

    return { email, name, decoded };
  };

  const handleLogin = () => {
    const loginRequest = {
      scopes: [process.env.REACT_APP_CLIENT_ID + "/.default"],
    };
    instance?.loginRedirect(loginRequest);
  };
  const processLogin = (response) => {
    localStorage.setItem("accessToken", response.accessToken);
    const { email, name } = extractUserInfoFromToken(response.accessToken);
    localStorage.setItem("userEmail", email || "");
    localStorage.setItem("userName", name || "");

    const isExpired = checkTokenExpiry(response.accessToken);
    if (isExpired) {
      localStorage.clear();
      instance.logoutRedirect();
    } else {
      getUserRoles();
    }
  };

  const getUserRoles = async () => {
    try {
      const responseHeaderRoles = await dispatch(getHeaderRoles({
        dynamicEndpoint: getUserRoleEndpoint,
      }));

      if (responseHeaderRoles?.meta?.requestStatus === 'fulfilled') {
        const roles = responseHeaderRoles?.payload.role;

        if (roles && roles !== "") {
          localStorage.setItem("userRole", roles);
          setUserRole(roles);
          setIsLoading(false); // Done loading
          const currentPath = window.location.pathname;
          const searchParams = new URLSearchParams(location.search);

          const latestCpsDate = searchParams.get("latestCpsDate");
          const latestDpsDate = searchParams.get("latestDpsDate");
          if (latestCpsDate || latestDpsDate) {
            dispatch(
              setForecastUpdates({
                latestCpsDate,
                latestDpsDate,
              })
            );
          }
          if ((currentPath === "/" || currentPath === "/login") && latestCpsDate === null && latestDpsDate === null) {
            navigate("/forecastCollabPage");
          } else if (latestCpsDate || latestDpsDate) {
            navigate(`/forecastCollabPage?latestCpsDate=${latestCpsDate}&latestDpsDate=${latestDpsDate}`);
          }
        } else {
          console.warn("No roles found for user.");
          setIsLoading(false); // Done loading even if no roles found
        }
      }
    } catch (error) {
      console.error("API error:", error);
      setIsLoading(false); // Done loading even if there's an error
    }
  };


  useEffect(() => {
    if (tabDetails.title === "forecastCollabPage") {
      setMenuItems(menuItems)
    }
  }, [tabDetails])

  const handleMenuItemClick = (path, label, item) => {
    item.redirect ? window.open(path, "_blank") : navigate(path);
    dispatch(setTitle(item));
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate("/forecastCollabPage");
    instance.logoutRedirect();
  };

  useEffect(() => {
    if (userRole === "Supplier Planner") {
      setMenuItems([
        // {
        //   id: 1,
        //   path: "/dashboard",
        //   label: "Dashboard",
        //   icon: "dashboard",
        //   title: "Dashboard",
        //   disabled:true
        // },
        {
          id: 2,
          path: "/forecastCollabPage",
          label: "Planning ",
          icon: "planning",
          title: "forecastCollabPage",
          disabled: false,
          redirect: false
        },
        {
          id: 6,
          path: "/poExceptionManagement",
          label: "PO Exception Management",
          icon: "poExceptionManagement",
          title: "PO Exception Management",
          disabled: false
        },
        {
          id: 3,
          path: "/reports",
          label: "Reports",
          icon: "reports",
          title: "Reports",
          disabled: false,
          redirect: false
        },
        // {
        //   id: 4,
        //   path: "/kpimetrics",
        //   label: "KPI Metrics",
        //   icon: "kpimetrics",
        //   title: "KPI Metrics",
        //   disabled: false,
        //   redirect: false
        // },
        // {
        //   id: 5,
        //   path: "/alerts",
        //   label: "Alerts",
        //   icon: "alerts",
        //   title: "Alerts",
        //   disabled: false,
        //   redirect: false
        // },
        // {
        //   id: 7,
        //   path: "/supplier-announcements",
        //   label: "Announcements",
        //   icon: "announcements",
        //   title: "Announcements",
        //   disabled: false,
        //   redirect: false
        // },
        // {
        //   id: 8,
        //   path: "/training",
        //   label: "Training Content",
        //   icon: "training",
        //   title: "Training Content",
        //   disabled: false,
        //   redirect: false
        // },
        // {
        //   id: 9,
        //   path: process.env.REACT_APP_POCOLLABORATION_URL,
        //   label: "Redirect to SAP Ariba PO collaboration",
        //   icon: "Redirect to SAP Ariba PO collaboration",
        //   title: "Redirect to SAP Ariba PO collaboration",
        //   disabled: false,
        //   redirect: true
        // }
      ]);
    }
    if (userRole === "App Admin") {
      setMenuItems([
        // {
        //   id: 1,
        //   path: "/dashboard",
        //   label: "Dashboard",
        //   icon: "dashboard",
        //   title: "Dashboard",
        //   disabled:true
        // },
        {
          id: 2,
          path: "/forecastCollabPage",
          label: "Planning ",
          icon: "planning",
          title: "forecastCollabPage",
          disabled: false,
          redirect: false
        },
        {
          id: 3,
          path: "/poExceptionManagement",
          label: "PO Exception Management",
          icon: "poExceptionManagement",
          title: "PO Exception Management",
          disabled: false
        },
        {
          id: 4,
          path: "/admin",
          label: "Admin",
          icon: "admin",
          title: "Admin",
          disabled: false,
          redirect: false
        },
        // {
        //   id: 5,
        //   path: "/reports",
        //   label: "Reports",
        //   icon: "reports",
        //   title: "Reports",
        //   disabled: false,
        //   redirect: false
        // },
        {
          id: 6,
          path: "/kpimetrics",
          label: "KPI Metrics",
          icon: "kpimetrics",
          title: "KPI Metrics",
          disabled: false,
          redirect: false
        },
        // {
        //   id: 7,
        //   path: "/alerts",
        //   label: "Alerts",
        //   icon: "alerts",
        //   title: "Alerts",
        //   disabled: false,
        //   redirect: false
        // },
        // {
        //   id: 8,
        //   path: "/admin-announcements",
        //   label: "Announcements",
        //   icon: "announcements",
        //   title: "Announcements",
        //   disabled: false,
        //   redirect: false
        // },
        // {
        //   id: 9,
        //   path: "/admin-training",
        //   label: "Training Content",
        //   icon: "training",
        //   title: "Admin Training",
        //   disabled: false,
        //   redirect: false
        // },
        {
          id: 9,
          path: process.env.REACT_APP_SUPPCONFIRM_URL,
          label: "Supplier Confirmation Analytics",
          icon: "supplierConfirmationAnalytics",
          title: "Supplier Confirmation Analytics",
          disabled: false,
          redirect: true
        }
      ]);
    }
    if (userRole === "Supplier Admin") {
      setMenuItems([
        // {
        //   id: 1,
        //   path: "/dashboard",
        //   label: "Dashboard",
        //   icon: "dashboard",
        //   title: "Dashboard",
        //   disabled:true
        // },
        {
          id: 2,
          path: "/forecastCollabPage",
          label: "Planning ",
          icon: "planning",
          title: "forecastCollabPage",
          disabled: false,
          redirect: false
        },
        {
          id: 9,
          path: "/poExceptionManagement",
          label: "PO Exception Management",
          icon: "poExceptionManagement",
          title: "PO Exception Management",
          disabled: false
        },
        // {
        //   id: 3,
        //   path: "/reports",
        //   label: "Reports",
        //   icon: "reports",
        //   title: "Reports",
        //   disabled: false,
        //   redirect: false
        // },
        // {
        //   id: 4,
        //   path: "/kpimetrics",
        //   label: "KPI Metrics",
        //   icon: "kpimetrics",
        //   title: "KPI Metrics",
        //   disabled: false,
        //   redirect: false
        // },
        // {
        //   id: 5,
        //   path: "/alerts",
        //   label: "Alerts",
        //   icon: "alerts",
        //   title: "Alerts",
        //   disabled: false,
        //   redirect: false
        // },
        {
          id: 6,
          path: "/admin",
          label: "Admin",
          icon: "admin",
          title: "Admin",
          disabled: false,
          redirect: false
        },
        // {
        //   id: 7,
        //   path: "/supplier-announcements",
        //   label: "Announcements",
        //   icon: "announcements",
        //   title: "Announcements",
        //   disabled: false,
        //   redirect: false
        // },
        // {
        //   id: 8,
        //   path: "/training",
        //   label: "Training Content",
        //   icon: "training",
        //   title: "Training Content",
        //   disabled: false,
        //   redirect: false
        // },
        // {
        //   id: 9,
        //   path: process.env.REACT_APP_POCOLLABORATION_URL,
        //   label: "Redirect to SAP Ariba PO collaboration",
        //   icon: "Redirect to SAP Ariba PO collaboration",
        //   title: "Redirect to SAP Ariba PO collaboration",
        //   disabled: false,
        //   redirect: true
        // }
      ]);
    }
    if (userRole === "Global Planner" || userRole === "Regional Planner" || userRole === "Global Viewer" || userRole === "Regional Viewer") {
      setMenuItems([
        // {
        //   id: 1,
        //   path: "/dashboard",
        //   label: "Dashboard",
        //   icon: "dashboard",
        //   title: "Dashboard",
        //   disabled:true
        // },
        {
          id: 2,
          path: "/forecastCollabPage",
          label: "Planning ",
          icon: "planning",
          title: "forecastCollabPage",
          disabled: false,
          redirect: false
        },
        {
          id: 2,
          path: "/poExceptionManagement",
          label: "PO Exception Management",
          icon: "poExceptionManagement",
          title: "PO Exception Management",
          disabled: false
        },
        // {
        //   id: 3,
        //   path: "/reports",
        //   label: "Reports",
        //   icon: "reports",
        //   title: "Reports",
        //   disabled: false,
        //   redirect: false
        // },
        {
          id: 4,
          path: "/kpimetrics",
          label: "KPI Metrics",
          icon: "kpimetrics",
          title: "KPIMetrics",
          disabled: false,
          redirect: false
        },
        // {
        //   id: 5,
        //   path: "/alerts",
        //   label: "Alerts",
        //   icon: "alerts",
        //   title: "Alerts",
        //   disabled: false,
        //   redirect: false
        // },
        // {
        //   id: 6,
        //   path: "/admin-announcements",
        //   label: "Announcements",
        //   icon: "announcements",
        //   title: "Announcements",
        //   disabled: false,
        //   redirect: false
        // },
        {
          id: 7,
          path: process.env.REACT_APP_SUPPCONFIRM_URL,
          label: "Supplier Confirmation Analytics",
          icon: "supplierConfirmationAnalytics",
          title: "Supplier Confirmation Analytics",
          disabled: false,
          redirect: true
        },
        // {
        //   id: 8,
        //   path: "/extract-forecast-from-sap",
        //   label: "Extract Forecast From SAP",
        //   icon: "extractForecastFromSAP",
        //   title: "Extract Forecast From SAP",
        //   disabled: false,
        //   redirect: false
        // }

      ]);
    }
  }, [tabDetails, userRole])

  useLayoutEffect(() => {
    let animationFrameId;

    const measure = () => {
      if (headerRef.current) {
        const height = headerRef.current.offsetHeight;

        if (height > 0) {
          setHeaderHeight(height);
        } else {
          animationFrameId = window.requestAnimationFrame(measure);
        }
      } else {
        animationFrameId = window.requestAnimationFrame(measure);
      }
    };

    animationFrameId = window.requestAnimationFrame(measure);

    window.addEventListener("resize", measure);

    return () => {
      window.removeEventListener("resize", measure);
      window.cancelAnimationFrame(animationFrameId);
    };
  }, [tabDetails]);

  return isLoading ? (
    <Loader /> // Show loader while loading
  ) : isAuthenticated && userRole ? (
    <Box
      sx={{
        margin: "auto",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
      }}
    >
      <PreventBackNavigation />
      <ThemeProvider theme={theme}>
        <Header
          userRole={userRole}
          headerRef={headerRef}
          title={title}
          handleLogout={handleLogout}
          handleMenuItemClick={handleMenuItemClick}
        />
        <Box
          sx={{
            paddingTop: `${headerHeight}px`,
            transition: "padding-top 0.3s ease",
          }}
        >
          <MainContent
            contentHeight={headerHeight}
            handleMenuItemClick={handleMenuItemClick}
            menuItems={menuItems}
          />
        </Box>
        <Footer />
        <ToastContainer />
      </ThemeProvider>
    </Box>
  ) : isAuthenticated && !userRole ? (
    <UnauthorizedApp logoutRedirect={handleLogout} />
  ) : (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <Loader />
    </div>
  );
}

export default App;
